import { formatApiMessage } from "./apiMessageFormat";
import { getDisplayLanguage } from "./language";
/**
 * The ONE authenticated fetch helper.
 *
 * Blueprint trap #1: a bare fetch() against the hub API always 401s, and mixing
 * "returns Response" with "returns parsed body" breaks callers silently. So:
 *
 *   - There is exactly one helper. No component calls fetch() directly.
 *   - It ALWAYS returns the parsed body (or undefined for 204). It never
 *     returns a Response. Do not add an option to change that.
 *   - Any non-2xx throws ApiError, which carries `status` so callers can branch
 *     on 403 without string-matching.
 *   - The access token is refreshed before every call, so a request never goes
 *     out with a token that expires mid-flight.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Parsed error body, when the server sent one. */
  readonly body: unknown;
  readonly originalMessage: string;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.originalMessage = message;
    Object.defineProperty(this, "message", {configurable: true, get: () => formatApiMessage(message, getDisplayLanguage())});
  }

  get isForbidden() {
    return this.status === 403;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  get isNotFound() {
    return this.status === 404;
  }

  get isStepUpRequired() {
    if (this.status !== 403 || !this.body || typeof this.body !== "object") return false;
    const body = this.body as Record<string, unknown>;
    return body.code === "step_up_required" || body.detail === "step_up_required"
      || (typeof body.detail === "object" && body.detail !== null
        && (body.detail as Record<string, unknown>).code === "step_up_required");
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

/** Returns a fresh access token, refreshing it if it is close to expiry. */
export type TokenSource = () => Promise<string>;

export interface ApiClient {
  get<T>(path: string, query?: Query): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  /** Authenticated binary download; returns decoded bytes, never a Response. */
  download(path: string): Promise<Blob>;
  /**
   * A multipart upload, for the one thing JSON cannot carry: a file.
   *
   * Its own method rather than `put(path, formData)` because the Content-Type
   * must be left ALONE — the browser writes it, including the multipart
   * boundary, and setting it by hand produces a body the server cannot parse.
   * Everything else — the token, the error unwrapping — is shared.
   */
  upload<T>(path: string, form: FormData, method?: "POST" | "PUT"): Promise<T>;
}

function withQuery(url: string, query?: Query): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined;
  const type = res.headers.get("content-type") ?? "";
  if (type.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return undefined;
    }
  }
  if (res.ok && type.includes("application/pdf")) return res.blob();
  const text = await res.text();
  return text.length ? text : undefined;
}

/** FastAPI puts the useful part in `detail`; unwrap it so toasts read well. */
function messageFrom(status: number, body: unknown, method: string, path: string): string {
  if (typeof body === "string" && body.trim()) return body.trim();
  if (body && typeof body === "object") {
    const detail = (body as Record<string, unknown>).detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (detail && typeof detail === "object" && "message" in detail && typeof detail.message === "string") return detail.message;
    // Pydantic validation errors arrive as a list of {loc, msg}.
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === "object" ? (d as Record<string, unknown>).msg : null))
        .filter((m): m is string => typeof m === "string");
      if (msgs.length) return msgs.join("; ");
    }
  }
  return `${method} ${path} failed (${status})`;
}

export function createApi(
  apiBase: string,
  getToken?: TokenSource,
  onStepUpRequired?: () => void,
  onUnauthorized?: () => void,
): ApiClient {
  async function csrf(): Promise<string> {
    const res = await fetch(`${apiBase}/auth/session`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    const body = await parseBody(res);
    if (!res.ok || !body || typeof body !== "object" || typeof (body as Record<string, unknown>).csrfToken !== "string") {
      const error = new ApiError(res.status, messageFrom(res.status, body, "GET", "/auth/session"), body);
      if (error.isUnauthorized) onUnauthorized?.();
      throw error;
    }
    return (body as { csrfToken: string }).csrfToken;
  }

  async function request<T>(method: string, path: string, opts: { body?: unknown; query?: Query } = {}): Promise<T> {
    const token = getToken ? await getToken() : null;
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (!getToken && method !== "GET" && method !== "HEAD") headers["X-CSRF-Token"] = await csrf();
    const init: RequestInit = { method, headers };
    if (!getToken) init.credentials = "same-origin";
    if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(opts.body);
    }

    const url = withQuery(`${apiBase}${path}`, opts.query);
    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (cause) {
      // Network-level failure: no status to report.
      throw new ApiError(0, `${method} ${path} could not reach the hub API`, cause);
    }

    const body = await parseBody(res);
    if (!res.ok) {
      const error = new ApiError(res.status, messageFrom(res.status, body, method, path), body);
      if (error.isStepUpRequired) onStepUpRequired?.();
      if (error.isUnauthorized) onUnauthorized?.();
      throw error;
    }
    return body as T;
  }

  async function upload<T>(path: string, form: FormData, method: "POST" | "PUT" = "PUT"): Promise<T> {
    const token = getToken ? await getToken() : null;
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (!getToken) headers["X-CSRF-Token"] = await csrf();
    let res: Response;
    try {
      res = await fetch(`${apiBase}${path}`, {
        method,
        credentials: getToken ? undefined : "same-origin",
        // No Content-Type: the browser supplies it with the boundary.
        headers,
        body: form,
      });
    } catch (cause) {
      throw new ApiError(0, `${method} ${path} could not reach the hub API`, cause);
    }
    const body = await parseBody(res);
    if (!res.ok) {
      const error = new ApiError(res.status, messageFrom(res.status, body, method, path), body);
      if (error.isStepUpRequired) onStepUpRequired?.();
      if (error.isUnauthorized) onUnauthorized?.();
      throw error;
    }
    return body as T;
  }

  return {
    get: (path, query) => request("GET", path, { query }),
    post: (path, body) => request("POST", path, { body }),
    patch: (path, body) => request("PATCH", path, { body }),
    put: (path, body) => request("PUT", path, { body }),
    del: (path) => request("DELETE", path),
    download: (path) => request<Blob>("GET", path),
    upload,
  };
}
